import { countryLabels } from '@/constant/countries';
import {
  BulkImportFieldError,
  BulkImportRow,
  BULK_IMPORT_JSON_FIELDS,
  BULK_IMPORT_MAX_ROWS,
  BULK_IMPORT_MIN_ROWS,
} from './harborBulkImport.types';

const ACCEPTED_COUNTRY_CODES = new Set(Object.keys(countryLabels));

export function createEmptyBulkImportRow(): BulkImportRow {
  return {
    id: crypto.randomUUID(),
    name: '',
    description: '',
    location: '',
    latitude: '',
    longitude: '',
    category: '',
    coastal_area: '',
    country_short_code: 'FI',
    min_depth: '',
    max_depth: '',
    berth_capacity: '',
  };
}

export function normalizeRowFromJson(raw: Record<string, unknown>): BulkImportRow {
  const row = createEmptyBulkImportRow();
  for (const field of BULK_IMPORT_JSON_FIELDS) {
    const value = raw[field];
    if (value === undefined || value === null) {
      continue;
    }
    row[field] = String(value).trim();
  }
  if (row.country_short_code) {
    row.country_short_code = row.country_short_code.toUpperCase();
  }
  return row;
}

export function parseJsonInput(text: string): {
  rows?: BulkImportRow[];
  error?: string;
} {
  const trimmed = text.trim();
  if (!trimmed) {
    return { error: 'JSON input is empty' };
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(trimmed);
  } catch {
    return { error: 'Invalid JSON syntax' };
  }

  if (!Array.isArray(parsed)) {
    return { error: 'JSON must be an array of harbour objects' };
  }

  if (parsed.length > BULK_IMPORT_MAX_ROWS) {
    return {
      error: `JSON array has ${parsed.length} rows; maximum is ${BULK_IMPORT_MAX_ROWS}`,
    };
  }

  const rows = parsed.map((item) => {
    if (!item || typeof item !== 'object' || Array.isArray(item)) {
      return createEmptyBulkImportRow();
    }
    return normalizeRowFromJson(item as Record<string, unknown>);
  });

  return { rows };
}

export async function readJsonFile(file: File): Promise<{
  rows?: BulkImportRow[];
  error?: string;
}> {
  if (!file.name.toLowerCase().endsWith('.json')) {
    return { error: 'Only .json files are supported' };
  }
  const text = await file.text();
  return parseJsonInput(text);
}

function trim(value: string): string | null {
  const t = value.trim();
  return t.length > 0 ? t : null;
}

function parseCoordinate(
  value: string,
  min: number,
  max: number,
  fieldName: string
): { valid: boolean; message?: string } {
  const str = value.trim();
  if (!str) {
    return { valid: false, message: `${fieldName} is required` };
  }
  if (!/^-?\d+(\.\d+)?$/.test(str)) {
    return { valid: false, message: `${fieldName} must be a decimal number` };
  }
  const num = Number(str);
  if (!Number.isFinite(num) || num < min || num > max) {
    return {
      valid: false,
      message: `${fieldName} must be between ${min} and ${max}`,
    };
  }
  return { valid: true };
}

function parseOptionalNumber(
  value: string,
  fieldName: string
): { valid: boolean; message?: string } {
  const str = value.trim();
  if (!str) {
    return { valid: true };
  }
  if (!/^-?\d+(\.\d+)?$/.test(str)) {
    return { valid: false, message: `${fieldName} must be a valid number` };
  }
  return { valid: true };
}

export function validateBulkImportRows(
  rows: BulkImportRow[],
  categoryKeys: Set<string>,
  coastalAreaNames: Set<string>
): BulkImportFieldError[] {
  const errors: BulkImportFieldError[] = [];

  if (rows.length < BULK_IMPORT_MIN_ROWS) {
    errors.push({
      row: 0,
      field: 'rows',
      message: `At least ${BULK_IMPORT_MIN_ROWS} row is required`,
    });
    return errors;
  }

  if (rows.length > BULK_IMPORT_MAX_ROWS) {
    errors.push({
      row: 0,
      field: 'rows',
      message: `Maximum ${BULK_IMPORT_MAX_ROWS} rows allowed`,
    });
    return errors;
  }

  const seen = new Set<string>();

  rows.forEach((row, index) => {
    const rowNum = index + 1;
    const name = trim(row.name);
    const location = trim(row.location);

    if (!name) {
      errors.push({ row: rowNum, field: 'name', message: 'Name is required' });
    }
    if (!trim(row.description)) {
      errors.push({
        row: rowNum,
        field: 'description',
        message: 'Description is required',
      });
    }
    if (!location) {
      errors.push({
        row: rowNum,
        field: 'location',
        message: 'Location is required',
      });
    }

    const lat = parseCoordinate(row.latitude, -90, 90, 'latitude');
    if (!lat.valid) {
      errors.push({
        row: rowNum,
        field: 'latitude',
        message: lat.message!,
      });
    }

    const lng = parseCoordinate(row.longitude, -180, 180, 'longitude');
    if (!lng.valid) {
      errors.push({
        row: rowNum,
        field: 'longitude',
        message: lng.message!,
      });
    }

    const category = trim(row.category);
    if (!category) {
      errors.push({
        row: rowNum,
        field: 'category',
        message: 'Category is required',
      });
    } else if (!categoryKeys.has(category)) {
      errors.push({
        row: rowNum,
        field: 'category',
        message: `Invalid category: ${category}`,
      });
    }

    const coastal = trim(row.coastal_area);
    if (!coastal) {
      errors.push({
        row: rowNum,
        field: 'coastal_area',
        message: 'Coastal area is required',
      });
    } else if (!coastalAreaNames.has(coastal)) {
      errors.push({
        row: rowNum,
        field: 'coastal_area',
        message: `Invalid coastal area: ${coastal}`,
      });
    }

    const country = trim(row.country_short_code)?.toUpperCase() ?? '';
    if (!country) {
      errors.push({
        row: rowNum,
        field: 'country_short_code',
        message: 'Country code is required',
      });
    } else if (!ACCEPTED_COUNTRY_CODES.has(country)) {
      errors.push({
        row: rowNum,
        field: 'country_short_code',
        message: `Invalid country code: ${country}`,
      });
    }

    const minDepth = parseOptionalNumber(row.min_depth, 'min_depth');
    if (!minDepth.valid) {
      errors.push({
        row: rowNum,
        field: 'min_depth',
        message: minDepth.message!,
      });
    }

    const maxDepth = parseOptionalNumber(row.max_depth, 'max_depth');
    if (!maxDepth.valid) {
      errors.push({
        row: rowNum,
        field: 'max_depth',
        message: maxDepth.message!,
      });
    }

    const berth = parseOptionalNumber(row.berth_capacity, 'berth_capacity');
    if (!berth.valid) {
      errors.push({
        row: rowNum,
        field: 'berth_capacity',
        message: berth.message!,
      });
    } else if (row.berth_capacity.trim()) {
      const n = Number(row.berth_capacity);
      if (!Number.isInteger(n) || n < 0) {
        errors.push({
          row: rowNum,
          field: 'berth_capacity',
          message: 'Berth capacity must be a non-negative integer',
        });
      }
    }

    if (name && location) {
      const key = `${name.toLowerCase()}|${location.toLowerCase()}`;
      if (seen.has(key)) {
        errors.push({
          row: rowNum,
          field: 'name',
          message: 'Duplicate name and location in this batch',
        });
      } else {
        seen.add(key);
      }
    }
  });

  return errors;
}

export function rowsToApiPayload(rows: BulkImportRow[]) {
  return rows.map((row) => ({
    name: row.name.trim(),
    description: row.description.trim(),
    location: row.location.trim(),
    latitude: row.latitude.trim(),
    longitude: row.longitude.trim(),
    category: row.category.trim(),
    coastal_area: row.coastal_area.trim(),
    country_short_code: row.country_short_code.trim().toUpperCase(),
    ...(row.min_depth.trim() ? { min_depth: Number(row.min_depth) } : {}),
    ...(row.max_depth.trim() ? { max_depth: Number(row.max_depth) } : {}),
    ...(row.berth_capacity.trim()
      ? { berth_capacity: Number(row.berth_capacity) }
      : {}),
  }));
}

export function getRowFieldErrors(
  errors: BulkImportFieldError[],
  rowIndex: number
): Record<string, string> {
  const rowNum = rowIndex + 1;
  const map: Record<string, string> = {};
  errors
    .filter((e) => e.row === rowNum)
    .forEach((e) => {
      map[e.field] = e.message;
    });
  return map;
}
